import React from 'react';
import dayjs from 'dayjs';
import type { InspectionReleaseNote } from '@/services/qc-workflow.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

function dash(v: string | null | undefined): string {
  if (v == null || String(v).trim() === '') return '—';
  return String(v);
}

function fmtDate(v?: string | null): string {
  if (!v) return '';
  return dayjs(v).format('DD MMM YYYY');
}

export type InspectionReleaseNotePrintViewProps = {
  irn?: InspectionReleaseNote;
  data?: InspectionReleaseNote;
  printedAt?: string;
  resolveUser?: (id?: string | null) => string;
};

/** Inspection release note — shared pagination engine with restored release/traceability meta. */
export function InspectionReleaseNotePrintView(props: InspectionReleaseNotePrintViewProps) {
  const irn = (props.irn || props.data || (props as any)) as InspectionReleaseNote;
  const resolve = props.resolveUser ?? ((_id?: string | null) => '—');
  const printedAt = props.printedAt || new Date().toISOString();
  const woNum =
    irn.workOrder?.work_order_number || irn.deliveryNote?.workOrder?.work_order_number;
  const dnNum = irn.deliveryNote?.delivery_note_number;
  const firNum = irn.fir?.fir_number;
  const dn = irn.deliveryNote;
  const cust = dn?.customer;
  const items = dn?.items ?? [];

  return (
    <EngineCommercialA4Print
      docType="inspection_release_note"
      title="INSPECTION RELEASE NOTE"
      documentNumber={irn.irn_number || 'IRN'}
      partyName={cust?.customer_name || ''}
      metaLines={[
        { label: 'IRN number', value: dash(irn.irn_number) },
        { label: 'Status', value: dash(irn.status) },
        {
          label: 'Release quantity',
          value:
            irn.release_quantity != null && !Number.isNaN(Number(irn.release_quantity))
              ? Number(irn.release_quantity).toFixed(4)
              : '',
        },
        { label: 'Release date', value: fmtDate(irn.release_date) },
        { label: 'Created', value: fmtDate(irn.created_at) },
        {
          label: 'Released by',
          value: irn.releaser?.name || resolve(irn.released_by),
        },
        {
          label: 'Approved by',
          value: irn.approver?.name || resolve(irn.approved_by),
        },
        { label: 'Approved date', value: fmtDate(irn.approved_date) },
        { label: 'Work order', value: dash(woNum) },
        { label: 'Delivery note', value: dash(dnNum) },
        { label: 'FIR', value: dash(firNum) },
        {
          label: 'Customer',
          value: cust
            ? `${cust.customer_name || ''}${cust.customer_code ? ` (${cust.customer_code})` : ''}`
            : '',
        },
        { label: 'Customer order', value: dn?.salesOrder?.sales_order_number || '' },
        { label: 'Customer PO', value: dn?.customer_po_number || '' },
        { label: 'Ship-to', value: (dn?.delivery_address || '').trim() },
      ].filter((m) => m.value && m.value !== '—')}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'description', label: 'Item / Description' },
        { key: 'qty', label: 'Qty', align: 'right' },
      ]}
      lines={(items.length
        ? items
        : [
            {
              item_description: 'Release quantity',
              quantity: irn.release_quantity,
            } as any,
          ]
      ).map((it: any, idx: number) => ({
        lineNumber: idx + 1,
        description: it.item_description || it.description || '—',
        cells: {
          qty: String(it.quantity ?? it.delivered_quantity ?? it.released_quantity ?? ''),
        },
      }))}
      finals={[
        ...(irn.rejection_reason ? [{ id: 'rejection', heightPx: 40 }] : []),
        ...(irn.notes ? [{ id: 'notes', heightPx: 40 }] : []),
        { id: 'printed', heightPx: 28 },
      ]}
      finalBlocks={{
        rejection: irn.rejection_reason ? (
          <p>
            <strong>Rejection reason:</strong> {dash(irn.rejection_reason)}
          </p>
        ) : null,
        notes: irn.notes ? (
          <p>
            <strong>Notes:</strong> {dash(irn.notes)}
          </p>
        ) : null,
        printed: (
          <p style={{ fontSize: 10, color: '#444' }}>
            Printed: {dayjs(printedAt).format('DD MMM YYYY HH:mm')}
          </p>
        ),
      }}
    />
  );
}
